import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle, ArrowLeft } from "lucide-react";
import Header from "../Header";
import Footer from "../Footer";
import WhatsAppFloat from "../WhatsAppFloat";
import PageMeta from "../PageMeta";
import { getPersonaBySlug, personaPages } from "../../data/pageConfig";

export default function PersonaPage() {
  const { personaSlug, lang } = useParams();
  const { t } = useTranslation("personas");
  const navigate = useNavigate();
  const persona = getPersonaBySlug(personaSlug);

  if (!persona) {
    navigate(`/${lang || "en"}`, { replace: true });
    return null;
  }

  const Icon = persona.icon;
  const prefix = lang ? `/${lang}` : "";

  const challenges = t(`${persona.key}.challenges`, { returnObjects: true });
  const features = t(`${persona.key}.features`, { returnObjects: true });

  return (
    <div className="min-h-screen bg-white">
      <PageMeta
        title={`${t(`${persona.key}.title`)} — TrackTeam`}
        description={t(`${persona.key}.description`)}
      />
      <Header />

      {/* Hero */}
      <section className="relative pt-24 md:pt-32 pb-16 md:pb-24 bg-gradient-to-br from-slate-50 via-blue-50/40 to-purple-50/30 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400/10 rounded-full blur-3xl animate-pulse"></div>
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6">
          <Link to={`${prefix}`} className="inline-flex items-center gap-2 text-[#62329f] hover:underline mb-6 text-sm font-medium">
            <ArrowLeft className="w-4 h-4" />
            {t("backToHome")}
          </Link>
          <div className="flex items-center gap-4 mb-6">
            <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${persona.gradient} flex items-center justify-center ${persona.shadowColor} shadow-lg`}>
              <Icon className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 leading-tight">
              {t(`${persona.key}.title`)}
            </h1>
          </div>
          <p className="text-xl text-gray-600 max-w-3xl leading-relaxed">
            {t(`${persona.key}.description`)}
          </p>
        </div>
      </section>

      {/* Daily Challenges */}
      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            {t(`${persona.key}.challengesTitle`)}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {Array.isArray(challenges) &&
              challenges.map((ch, i) => (
                <div key={i} className="bg-amber-50 rounded-2xl p-6 border border-amber-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{ch.title}</h3>
                  <p className="text-gray-600">{ch.text}</p>
                </div>
              ))}
          </div>
        </div>
      </section>

      {/* Features That Matter */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            {t(`${persona.key}.featuresTitle`)}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Array.isArray(features) &&
              features.map((feat, i) => (
                <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100 flex gap-4 items-start">
                  <CheckCircle className="w-6 h-6 text-[#62329f] flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{feat.title}</h3>
                    <p className="text-gray-600 text-sm">{feat.text}</p>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-[#62329f] via-purple-700 to-indigo-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{t(`${persona.key}.ctaTitle`)}</h2>
          <p className="text-lg text-purple-100 mb-8 max-w-2xl mx-auto">{t(`${persona.key}.ctaSubtitle`)}</p>
          <div className="flex justify-center gap-4">
            <a href="https://apps.apple.com/ae/app/trackteam/id6755401884" target="_blank" rel="noreferrer">
              <img src="/Image/logo_appstore.svg" alt="App Store" className="h-12 w-[162px] object-contain" loading="lazy" />
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.washey.familytrack.trackteam&pcampaignid=web_share" target="_blank" rel="noreferrer">
              <img src="/Image/logo_playstore.svg" alt="Google Play" className="h-12 w-[162px] object-contain" loading="lazy" />
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
